<template>
    <Loader v-if="loading"/>

    <form 
        v-if="currentPhase"
        @submit="formSubmitHandler($event)" 
        action="" 
        method="post" 
        enctype="multipart/form-data"
        id="ppppForm"
        class="pppp-form"
        :class="{'was-validated': wasValidated}" 
        novalidate>

        <fieldset id="phase-1" class="pppp-fieldset">
            <legend>1. Lähettäjän tiedot</legend>
            <div class="row">
                <div class="col-sm-6 col-lg-4">
                    <FormInput v-model="formData.firstName" id="firstName" label="Etunimi" :required="true" />
                </div>
                <div class="col-sm-6 col-lg-4">
                    <FormInput v-model="formData.lastName" id="lastName" label="Sukunimi" :required="true" />
                </div>
                <div class="col-sm-6 col-lg-4">
                    <FormInput v-model="formData.email" id="email" type="email" label="Sähköposti" :required="true" />
                </div>
                <div class="col-sm-6 col-lg-4">
                    <FormInput v-model="formData.tel" id="tel" type="tel" label="Puhelin" />
                </div>
                <div class="col-sm-6 col-lg-4">
                    <FormInput v-model="formData.occupation" id="occupation" label="Ammatti" />
                </div>
            </div> <!-- /.row -->
        </fieldset>

        <fieldset id="phase-2" class="pppp-fieldset" :disabled="phaseDisabled(2)">
            <legend>2. Produktion / aiheen tiedot</legend>
            <transition name="fade">
                <div v-if="! phaseDisabled(2)">

                    <div class="row">
                        <div class="col-sm-6 col-lg-4">
                            <FormInput v-model="formData.productionName" id="productionName" label="Produktion/aiheen nimi" :required="true" />
                        </div>
                        <div class="col-sm-6 col-lg-8">
                            <FormInput 
                                v-model="formData.relation"
                                id="relation" 
                                label="Lähettäjän suhde aineistoon/produktioon" 
                                placeholder="Esim. Esiintyjä/suunnittelija/ohjaaja/koreografi/valokuvaaja/muu" />
                        </div>
                    </div> <!-- /.row -->

                    <div class="row">
                        <div class="col-sm-4">
                            <FormInput v-model="formData.executor" id="executor" label="Toteuttaja (teatteri, ryhmä tms.)" />
                        </div>
                        <div class="col-sm-4">
                            <FormInput v-model="formData.place" id="place" label="Toteutuspaikka"  />
                        </div>
                        <div class="col-sm-4">
                            <FormInput v-model="formData.time" id="time" label="Toteutusajankohta" />
                        </div>
                    </div> <!-- /.row -->

                    <div class="row">
                        <div class="col-md-6">
                            <FormTextArea 
                                v-model="formData.addInfoAuthors" 
                                id="addInfoAuthors" 
                                label="Lisätietoa työryhmästä tai toteuttajista" />
                        </div>
                        <div class="col-md-6">
                            <FormTextArea 
                                v-model="formData.addInfoProduction" 
                                id="addInfoProduction" 
                                label="Muita huomioita produktiosta" />
                        </div>
                    </div> <!-- /.row -->

                </div>
            </transition>
        </fieldset>

        <fieldset id="phase-3" class="pppp-fieldset" :disabled="phaseDisabled(3)">
            <legend>3. Lähetettävät tiedostot</legend>
            <transition name="fade">
                <div v-if="! phaseDisabled(3)">

                    <FormFileData 
                        v-for="(file, index) in formData.files" 
                        :type="file.type"
                        :key="'file-'+index" 
                        :id="index" 
                        :hash="draftKey"
                        :invalidGroups="invalidGroups"
                        :maxFileSize="maxFileSize"
                        v-model="formData.files[index]" />

                    <div class="form-group pppp-add-file-buttons">
                        <button @click="addFile('image')" type="button" class="btn btn-primary">
                            <span class="glyphicon glyphicon-picture"></span>Lisää kuvatiedosto
                        </button> 
                        <button @click="addFile('video')" type="button" class="btn btn-primary">
                            <span class="glyphicon glyphicon-film"></span>Lisää videotiedosto
                        </button>
                        <button @click="addFile('audio')" type="button" class="btn btn-primary">
                            <span class="glyphicon glyphicon-volume-up"></span>Lisää äänitiedosto
                        </button>
                    </div>

                </div>
            </transition>
        </fieldset>

        <fieldset id="phase-4" class="pppp-fieldset" :disabled="phaseDisabled(4)">
            <legend>4. Lähetys</legend>
            <transition name="fade">
                <div v-if="! phaseDisabled(4)">

                    <FormCheckbox 
                        v-model="formData.acceptTerms" 
                        id="acceptTerms" 
                        label="Olen tutustunut tietosuojaselosteeseen ja hyväksyn, että tietojani säilytetään ja käytetään tietosuojaselosteen mukaisesti"
                        :required="true" 
                        style="margin:0;" />

                    <FormCheckbox 
                        v-model="formData.acceptContact" 
                        id="acceptContact" 
                        label="Minuun saa olla yhteydessä"
                        style="margin:0;" />

                </div>
            </transition>
        </fieldset>

        <div class="form-group text-center pppp-submit-btn-group">
            <button v-if="currentPhase > 2" type="button" @click="saveDraft" id="saveDraftBtn" class="btn btn-primary btn-lg" :disabled="submitDisabled">
                Tallenna luonnos
            </button>
            <button type="submit" id="submitBtn" class="btn btn-primary btn-lg" :disabled="submitDisabled">
                <span v-if="currentPhase < phasesCount">Jatka</span>
                <span v-if="currentPhase == phasesCount">Lähetä</span>
            </button>
        </div>

        <HelpModal :id="helpId" :title="helpTitle" />

        <Alerts :modalErrors="modalErrors" :modalMessages="modalMessages" />
        
    </form>

    <div v-if="!currentPhase">
        <div class="alert alert-info">
            Tiedot lähetettiin onnistuneesti.
        </div>
        <p>Kiitos osallistumisestasi ja lähettämistäsi aineistosta. Olemme vastaanottaneet aineiston ja tutustumme siihen mahdollisimman pian. Saat sähköpostiisi vahvistuksen aineiston lähettämisestä. Jos sinulla on kysyttävää lähettämääsi aineistoon liittyen, voit ottaa yhteyttä: ilona.kemppainen@teatterimuseo.fi</p>
    </div>

</template>

<script>
import axios from 'axios';
import Alerts from './components/Alerts.vue';
import FormCheckbox from './components/FormCheckbox.vue';
import FormFileData from './components/FormFileData.vue';
import FormInput from './components/FormInput.vue';
import FormTextArea from './components/FormTextArea.vue';
import HelpModal from './components/HelpModal.vue';
import Loader from './components/Loader.vue';

export default {
    name: 'App',
    components: {
        Alerts,
        FormCheckbox,
        FormFileData,
        FormInput,
        FormTextArea,
        HelpModal,
        Loader
    },
    computed: {
        /**
         * @return  {string}
         */
        draftUrl() {
            return location.protocol + '//' + location.host + location.pathname + '?draft=' + this.draftKey;
        },
        /**
         * @return  {boolean}
         */
        submitDisabled() {
            return this.uploadingCount > 0;
        },
        /**
         * @return  {string}
         */
        maxFileSize() {
            if (this.serverConfig) {
                let postMaxSize = parseInt(this.serverConfig.post_max_size);
                let uploadMaxFileSize = parseInt(this.serverConfig.upload_max_filesize);
                if (uploadMaxFileSize < postMaxSize) {
                    return this.serverConfig.upload_max_filesize;
                }
                return this.serverConfig.post_max_size;
            }
            return null;
        }
    },
    data() {
        return {
            apiUrl: process.env.VUE_APP_SERVER_PATH + 'wp-content/plugins/teatterimuseo-participation-project/api/',
            loading: true,
            serverConfig: null,
            uploadingCount: 0,
            currentPhase: 1,
            phasesCount: 4,
            wasValidated: false,
            helpId: null,
            helpTitle: null,
            draftKey: null,
            savingDraft: false,
            formData: {
                firstName: null,
                lastName: null,
                email: null,
                tel: null,
                occupation: null,
                productionName: null,
                relation: null,
                executor: null,
                place: null,
                time: null,
                addInfoAuthors: null,
                addInfoProduction: null,
                files: [],
                acceptTerms: false,
                acceptContact: false,
                created: null,
                modified: null,
                status: 0
            },
            defaultFileData: {
                id: null,
                fileName: null,
                type: null,
                originalFileName: null,
                description: null,
                originalFormat: null,
                time: null,
                place: null,
                author: null,
                authorInfo: null,
                authorUnknown: false,
                authorPermission: false,
                otherCopyrightHolder: null,
                addInfo: null,
                howIGotIt: null
            },
            invalidGroups: [],
            modalErrors: [],
            modalMessages: []
        }
    },
    created() {
        // Get draft key from URL
        let uri = window.location.search.substring(1); 
        let params = new URLSearchParams(uri);
        this.draftKey = params.get('draft');

        // Event listener for openHelpModal event
        this.$emitter.on('openHelpModal', (helpOptions) => {
            //console.log(helpId);
            this.helpId = helpOptions.id;
            this.helpTitle = helpOptions.title;
            // Modal is opened via data attributes
            //window.$('#helpModal').modal();
        });

        // Event listener for validateCustomFields event
        this.$emitter.on('validateCustomFields', () => {
            this.validateCustomFields();
        });

        // Event listener for deleteFile event
        this.$emitter.on('deleteFile', (index) => {
            if (this.formData.files[index].fileName) {
                // File already uploaded, send delete request
                this.deleteFile(index);
            } else {
                // File not yet uploaded, just splice
                this.formData.files.splice(index, 1);
            }
        });

        // Event listener for uploadFile event
        this.$emitter.on('uploadFile', (data) => {
            this.uploadingCount++;
            this.uploadFile(data);
        });

        // Event listener for dismissModalError event
        this.$emitter.on('dismissModalError', (index) => {
            this.modalErrors.splice(index, 1);
        });

        // Event listener for dismissModalMessage event
        this.$emitter.on('dismissModalMessage', (index) => {
            this.modalMessages.splice(index, 1);
        });
    },
    mounted() {
        // Load server config first to get file upload limits
        this.loadConfig();
    },
    methods: {
        /**
         * @param   {number}    phaseId
         * @return  {boolean}
         */
        phaseDisabled(phaseId) {
            return phaseId > this.currentPhase;
        },
        /**
         * Loads needed server config values.
         */
        loadConfig() {
            axios({
                method: 'get',
                url: this.apiUrl + 'server-config'
            })
            .then((response) => {
                //console.log(response);
                if (response.data.status == 'SUCCESS') {
                    this.serverConfig = response.data.config;
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .then(() => {
                if (this.draftKey) {
                    this.loadDraft(this.draftKey);
                } else {
                    this.loading = false;
                } 
            });
        },
        /**
         * Adds a new file object to this.formData.files.
         * @param   {string}    type
         */
        addFile(type) {
            let file = Object.assign({}, this.defaultFileData);
            file.type = type;
            this.formData.files.push(file);
            // Reset validation messages when adding new file
            this.wasValidated = false;
            this.invalidGroups = [];
        },
        /**
         * Validates custom stuff like groups.
         * @return  {boolean}
         */
        validateCustomFields() {
            let customFieldsValid = true;
            // Reset invalid groups always before validation
            this.invalidGroups = [];
            //console.log(this.formData);
            // Phase 2 custom validations
            if (this.currentPhase > 1) {
                // Need to select at least one from group author
                this.formData.files.forEach((item, index) => {
                    if (! item.author && ! item.authorUnknown) {
                        this.invalidGroups.push('file-'+index+'-author');
                        customFieldsValid = false;
                    }
                });
            }
            return customFieldsValid;
        },
        saveDraft() {
            this.savingDraft = true;
            document.getElementById('ppppForm').submit();
        },
        /**
         * Handles form validation and proceeding to next phase or calling postForm when done.
         */
        formSubmitHandler(e) {
            e.preventDefault();
            e.stopPropagation();

            // Validate form
            let validity = e.target.checkValidity();
            let customValidity = this.validateCustomFields();
            
            if (validity && customValidity) {
                // Form is valid so far, reset wasValidated
                this.wasValidated = false;
                
                if (this.savingDraft) {
                    this.formData.status = 0;
                    this.postForm();
                } else {
                    if (this.currentPhase == this.phasesCount) {
                        // Submit
                        this.formData.status = 1;
                        this.postForm();
                    } else if (this.currentPhase == 2)  {
                        // Save draft automatically before proceeding to phase 3
                        this.postForm();
                    } else {
                        // Next phase
                        this.currentPhase++;
                    }
                }
            } else {
                // Not valid, show errors
                this.wasValidated = true;
                // Scroll to first invalid field
                let el = document.querySelector(':invalid:not(form):not(fieldset)');
                if (el) {
                    el.scrollIntoView({behavior: 'smooth'});
                }
            }
        },
        /**
         * 
         */
        postForm() {
            axios({
                method: 'post',
                url: this.apiUrl + 'post',
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                data: this.createFormData()
            })
            .then((response) => {
                //console.log(response);
                if (response.data.errors && response.data.errors.length) {
                    this.modalErrors = response.data.errors;
                } else {
                    // Get id and hash on the first save draft
                    if (! this.draftKey) {
                        this.draftKey = response.data.post.hash;
                        //console.log('hash set: ' + this.draftKey);
                    }
                    if (this.currentPhase < 3) {
                        this.currentPhase++;
                    } else {
                        if (this.formData.status == 0) {
                            this.formData = response.data.post;
                            this.modalMessages.push('Luonnos tallennettu. Linkki luonnoksen muokkaukseen on lähetetty sähköpostiisi.');
                        } else {
                            // Setting currentPhase null will hide the form and show the thanks message
                            this.currentPhase = null;
                        }
                        this.sendMail();
                    }
                }
            })
            .catch((error) => {
                console.error(error);
                this.modalErrors.push('Valitettavasti jokin meni mönkään :(');
            })
            .then(() => {
                this.savingDraft = false;
            });
        },
        sendMail() {
            let formData = new FormData();
            formData.append('status', this.formData.status);
            formData.append('hash', this.draftKey);
            formData.append('draftUrl', this.draftUrl);
            formData.append('email', this.formData.email);
            axios({
                method: 'post',
                url: this.apiUrl + 'send-mail',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                data: formData
            })
            .then((response) => {
                //console.log(response);
                if (response.data.errors && response.data.errors.length) {
                    this.modalErrors = response.data.errors;
                }
            })
            .catch((error) => {
                console.error(error);
            });
        },
        /**
         * Uploads a file to the server and saves it's data to the db.
         * @param   {object}    data
         */
        uploadFile(data) {
            let formData = new FormData();

            formData.append('uploadFile', data.fileInput.files[0]);
            formData.append('hash', this.draftKey);
            formData.append('fileType', data.fileType);
            
            axios({
                method: 'post',
                url: this.apiUrl + 'file',
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                data: formData
            })
            .then((response) => {
                //console.log(response);
                if (response.data.errors && response.data.errors.length) {
                    this.modalErrors = response.data.errors;
                } else {
                    this.formData.files[data.index].fileName = response.data.fileName;
                    this.formData.files[data.index].originalFileName = response.data.originalFileName;
                }
            })
            .catch((error) => {
                console.error(error);
                this.modalErrors.push('Tiedoston lataaminen epäonnistui');
            })
            .then(() => {
                this.uploadingCount--;
            });
        },
        /**
         * Creates FormData object for posting.
         * @return  {object}
         */
        createFormData() {
            let formData = new FormData();

            //console.log(this.formData);

            for (let key in this.formData) {
                if (this.formData[key] !== null) {
                    if (key == 'files') {
                        for (let i = 0; i < this.formData[key].length; i++) {
                            // Append file data
                            formData.append('files['+i+']', JSON.stringify(this.formData[key][i]));
                        }
                    } else {
                        formData.append(key, this.formData[key]);
                    }
                } else {
                    // Post null values as empty strings; otherwise they'll post as "null" strings
                    formData.append(key, '');
                }
            }
            // Add hash to form data if draftKey set
            if (this.draftKey) {
                formData.append('hash', this.draftKey);
            }

            return formData;
        },
        /**
         * Loads a draft post from the db.
         * @param   {string}    draftKey
         */
        loadDraft(draftKey) {
            axios({
                method: 'get',
                url: this.apiUrl + 'post',
                params: {
                    hash: draftKey
                }
            })
            .then((response) => {
                //console.log(response);
                let draft = response.data.post;

                if (! draft.id) {
                    this.modalErrors.push('Linkki on virheellinen tai luonnos poistettu.');
                    this.draftKey = null;
                } else if (draft.status > 0) {
                    this.modalErrors.push('Tiedot on jo lähetetty eikä niitä voi enää muokata.');
                    this.draftKey = null;
                } else {
                    this.formData = draft;
                    this.currentPhase = 2;
                }
                // Go to phase 3 if there are any files added already
                if (draft.files.length) {
                    this.currentPhase = 3;
                }
                // Go to phase 4 if there are terms accepted already
                if (draft.acceptTerms || draft.acceptContact) {
                    this.currentPhase = 4;
                }

                this.loading = false;
            })
            .catch((error) => {
                console.error(error);
                this.modalErrors.push('Tietojen lataaminen epäonnistui');
            });
        },
        /**
         * Deletes a file from this.formData.files, 
         * the file from the server and it's data from the db.
         * @param   {number}    index
         */
        deleteFile(index) {
            this.loading = true;
            axios({
                method: 'delete',
                url: this.apiUrl +'file',
                params: {
                    hash: this.draftKey,
                    fileType: this.formData.files[index].type,
                    fileId: this.formData.files[index].id,
                    fileName: this.formData.files[index].fileName
                },
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
            .then((response) => {
                //console.log(response);
                if (response.data.status == 'SUCCESS') {
                    this.formData.files.splice(index, 1);
                }
                if (response.data.errors && response.data.errors.length) {
                    this.modalErrors = response.data.errors;
                }
                if (response.data.messages && response.data.messages.length) {
                    this.modalMessages = response.data.messages;
                }
            })
            .catch((error) => {
                console.error(error);
                this.modalErrors.push('Tiedoston poistaminen epäonnistui');
            })
            .then(() => {
                this.loading = false;
            });
        }
    }
}
</script>

<style>
.pppp-fieldset {
    margin: 2rem 0;
}
.pppp-fieldset:disabled {
    opacity: .25;
}
.pppp-fieldset legend {
    font-size: 1.25rem;
}
.pppp-form .form-control[disabled], 
.pppp-form .form-control[readonly], 
.pppp-form fieldset[disabled] .form-control {
    background-color: #ccc;
}
.pppp-form .form-group {
    margin-bottom: 1rem;
}
.pppp-form .form-group.disabled,
.pppp-form .checkbox.disabled {
    opacity: .5;
}
.pppp-h2 {
    font-size: 18px;
}
.pppp-label {
    font-size: 14px;
    font-weight: normal;
}
.pppp-textarea {
    font-size: 14px !important;
}
.pppp-add-file-buttons .btn {
    margin-right: 10px;
}
.pppp-add-file-buttons .glyphicon {
    margin-right: 5px;
}
.pppp-add-file-buttons .glyphicon-volume-up {
    position: relative;
    top: 2px;
}
.pppp-submit-btn-group .btn + .btn {
    margin-left: 10px;
}
.pppp-validate-group-invalid {
    background-color: #200;
    margin-bottom: 20px;
    border-radius: 1px;
    box-shadow: 0 0 0 10px #200;
}
.pppp-validation-msg {
    display: none;
    color: #c00;
    font-size: 14px;
    margin-top: .25rem;
}
.was-validated .pppp-input:invalid + .pppp-validation-msg,
.was-validated .pppp-textarea:invalid + .pppp-validation-msg,
.was-validated .pppp-select:invalid + .pppp-validation-msg,
.was-validated .pppp-input:invalid + .bootstrap-filestyle + .pppp-validation-msg,
.pppp-validate-group-invalid .pppp-validation-msg,
.was-validated .checkbox.invalid {
    display: block;
}
.was-validated .checkbox.invalid .pppp-validation-msg {
    display: inline;
}
.pppp-validation-msg .glyphicon-exclamation-sign {
    top: 2px;
}
.pppp-file-block {
    margin-bottom: 20px;
}
.pppp-textarea {
    resize: vertical;
}
.was-validated .pppp-input:invalid,
.was-validated .pppp-select:invalid,
.was-validated input[type="file"]:invalid + .bootstrap-filestyle input {
    background-color: #fcc !important;
    color: #c00 !important;
    border-color: #c00 !important;
}
.was-validated .pppp-input:invalid::placeholder,
.was-validated input[type="file"]:invalid + .bootstrap-filestyle input::placeholder {
    color: #c00 !important;
}
.btn:focus, .btn:active:focus, .btn.active:focus, .btn.focus, .btn:active.focus, .btn.active.focus {
    outline: 1px dashed #666;
    outline-offset: 2px;
}
.bootstrap-filestyle .glyphicon {
    margin-right: 5px;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity .5s;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
/* To make the Loader behave correctly */
.box.bg-grey {
    position: relative;
}

@media (max-width: 767px) {
    .pppp-add-file-buttons .btn {
        display: block;
        width: 100%;
        margin-bottom: 10px;
    }
}
</style>
